import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { User } from "@shared/models/auth";

type AuthUser = User & {
  teamMemberName?: string | null;
  permissions?: string[];
  canRegisterTasks?: boolean;
};

const ROLE_LEVELS: Record<string, number> = {
  member: 0,
  md_admin: 0,
  c_suite: 0,
  general_admin: 1,
  hr_admin: 2,
  it_admin: 3,
  super_admin: 4,
  dev_admin: 5,
};

const SDF_MD_NAME = "SDF MD";
const SDF_PMO_NAME = "SDF PMO";
const SDF_BOARD_SECRETARY_NAME = "SDF Board Secretary";

function getRoleLevel(role: string): number {
  if (role === "admin") return ROLE_LEVELS.general_admin;
  return ROLE_LEVELS[role] ?? 0;
}

function isAtLeastRole(userRole: string | undefined | null, minimumRole: string): boolean {
  return getRoleLevel(userRole ?? "member") >= (ROLE_LEVELS[minimumRole] ?? 0);
}

async function fetchUser(): Promise<AuthUser | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function logoutRequest(): Promise<void> {
  await fetch("/api/logout", {
    method: "POST",
    credentials: "include",
  });
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
      // Hard navigation to "/" wipes the URL bar (so the next user logging
      // in on the same browser doesn't inherit the previous user's last
      // visited page like /admin) AND clears all in-memory react-query
      // cache so no data leaks across sessions.
      window.location.assign("/");
    },
  });

  const role = user?.role ?? "member";
  const teamMemberName = user?.teamMemberName ?? null;
  const isMdAdmin = role === "md_admin";
  const isDevAdmin = role === "dev_admin";
  const hasTeamMemberBinding = !!user?.teamMemberId;
  const isSdfMdPrincipal = hasTeamMemberBinding && teamMemberName === SDF_MD_NAME;
  const isDispatchPrincipal = hasTeamMemberBinding && (teamMemberName === SDF_PMO_NAME || teamMemberName === SDF_BOARD_SECRETARY_NAME);

  const permissionSet = useMemo(() => new Set(user?.permissions ?? []), [user?.permissions]);

  function hasPermission(name: string): boolean {
    if (!user) return false;
    if (isDevAdmin) return true;
    return permissionSet.has(name);
  }

  // MD-review family layered with identity bindings (matrix OR binding).
  const canViewMdMeetings = hasPermission("view_md_meetings") || isSdfMdPrincipal;
  const canActOnMdReview = hasPermission("act_on_md_review") || isSdfMdPrincipal;
  const canMarkDispatched = hasPermission("mark_dispatched") || isDispatchPrincipal;
  const canViewAwaitingDispatch = hasPermission("view_awaiting_dispatch") || canViewMdMeetings || isDispatchPrincipal;

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    permissions: permissionSet,
    hasPermission,
    // Convenience flags — all derive from permissions (with the same names as before).
    isAdmin: hasPermission("view_admin_panel"),
    isGeneralAdmin: isAtLeastRole(role, "general_admin"),
    isMdAdmin,
    isHrAdmin: hasPermission("view_hr_dossier"),
    isItAdmin: hasPermission("view_settings_panel"),
    // isSuperAdmin is an IDENTITY check on the role name (super_admin or
    // dev_admin), NOT a permission lookup. Otherwise a custom role granted
    // `manage_governance` would silently inherit super-admin-only UI behavior
    // (e.g. unrestricted status transitions in action-item-detail). For
    // capability gates use hasPermission(...) directly.
    isSuperAdmin: role === "super_admin" || role === "dev_admin",
    isDevAdmin,
    isMember: !hasPermission("view_admin_panel") && !isMdAdmin,
    teamMemberName,
    canViewMdMeetings,
    canActOnMdReview,
    canMarkDispatched,
    canViewAwaitingDispatch,
    canManageRoles: hasPermission("manage_roles_permissions"),
    canConfirmDroppedTheBall: hasPermission("confirm_dropped_the_ball"),
    canReviseDueDate: hasPermission("revise_due_date"),
    canRegisterTasks: !!user?.canRegisterTasks || hasPermission("create_action_item"),
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
    roleLevel: getRoleLevel(role),
  };
}
